import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { MdClose, MdSearch } from 'react-icons/md';
import { useTranslation } from '../localization/useAppTranslation';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  searchText?: string;
}

interface SearchableDropdownProps {
  id: string;
  options: SearchableDropdownOption[];
  value?: string | null;
  placeholder: string;
  title?: string;
  className?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled?: boolean;
  maxVisibleOptions?: number;
  rightAdornment?: ReactNode;
  onChange: (value: string) => void;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  id,
  options,
  value,
  placeholder,
  title,
  className,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  maxVisibleOptions = 100,
  rightAdornment,
  onChange,
}) => {
  const { t } = useTranslation('common');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsListRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const focusSearchInput = () => {
    if (!searchInputRef.current) {
      return;
    }

    try {
      searchInputRef.current.focus({ preventScroll: true });
    } catch {
      searchInputRef.current.focus();
    }
  };

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return options.slice(0, maxVisibleOptions);
    }

    return options
      .filter((option) => {
        const haystack = `${option.label} ${option.searchText || ''}`.toLowerCase();
        return haystack.includes(normalized);
      })
      .slice(0, maxVisibleOptions);
  }, [maxVisibleOptions, options, query]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      focusSearchInput();

      // Keep the current selection visible and aligned near the top when opening.
      requestAnimationFrame(() => {
        const optionsList = optionsListRef.current;
        const selectedButton = optionsList?.querySelector<HTMLButtonElement>(
          '.searchable-dropdown-option.is-selected',
        );

        if (!optionsList || !selectedButton) {
          return;
        }

        const maxScrollTop = optionsList.scrollHeight - optionsList.clientHeight;
        const targetScrollTop = Math.min(selectedButton.offsetTop, Math.max(maxScrollTop, 0));
        optionsList.scrollTo({ top: targetScrollTop });
      });
    }
  }, [isOpen]);

  const openDropdown = () => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div
      ref={rootRef}
      className={`searchable-dropdown ${isOpen ? 'is-open' : ''} ${className || ''}`.trim()}
      title={title}
    >
      <button
        id={id}
        type="button"
        className="searchable-dropdown-trigger"
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-describedby={ariaDescribedBy}
      >
        <span className="searchable-dropdown-selected">
          {selectedOption?.label || placeholder}
        </span>
        {rightAdornment && (
          <span className="searchable-dropdown-adornment" aria-hidden="true">
            {rightAdornment}
          </span>
        )}
        <span className="searchable-dropdown-chevron" aria-hidden="true">
          <MdKeyboardArrowDown />
        </span>
      </button>

      {isOpen && (
        <div className="searchable-dropdown-panel">
          <div className="searchable-dropdown-search-wrap">
            {query ? (
              <button
                type="button"
                className="searchable-dropdown-search-icon-btn"
                onClick={() => {
                  setQuery('');
                  focusSearchInput();
                }}
                aria-label={t('dropdown.clearSearch')}
                title={t('dropdown.clearSearchText')}
              >
                <MdClose />
              </button>
            ) : (
              <span className="searchable-dropdown-search-icon" aria-hidden="true">
                <MdSearch />
              </span>
            )}
            <input
              ref={searchInputRef}
              type="text"
              className="searchable-dropdown-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('dropdown.searchPlaceholder')}
              aria-label={t('dropdown.searchLabel', { label: placeholder })}
            />
          </div>
          <ul
            ref={optionsListRef}
            className="searchable-dropdown-options"
            role="listbox"
            aria-labelledby={id}
          >
            {filteredOptions.length === 0 && (
              <li className="searchable-dropdown-empty" aria-live="polite">
                {t('dropdown.noMatchingOptions')}
              </li>
            )}
            {filteredOptions.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  className={`searchable-dropdown-option ${option.value === value ? 'is-selected' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    closeDropdown();
                  }}
                  role="option"
                  aria-selected={option.value === value}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
